import { Box, Stack, Tooltip, Typography } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { getAppControlStandardRuleOptions } from 'src/utils/enums/uiOnly/appControlStandardRule';
import { getBasePolicyTemplateLabel } from 'src/utils/enums/uiOnly/basePolicyTemplate';
import {
  getPolicyRuleOptionOptions,
  isPolicyRuleOptionValidInSupplemental,
} from 'src/utils/enums/uiOnly/policyRuleOption';

const STANDARD_RULES = getAppControlStandardRuleOptions();
const RULE_OPTIONS = getPolicyRuleOptionOptions();

const RULE_STATE = { On: 'on', Off: 'off', NotApplicable: 'notApplicable' };

const RULE_STATE_ICONS = {
  [RULE_STATE.On]: { icon: 'mdi:check', color: '#7DFF92' },
  [RULE_STATE.Off]: { icon: 'mdi:close', color: '#F28B82' },
  [RULE_STATE.NotApplicable]: { icon: 'mdi:minus', color: '#6B7280' },
};

const NOT_APPLICABLE_NOTE = 'Not recorded for this version; only a base the portal built has it.';
const SUPPLEMENTAL_NOTE = 'Only a base policy can set this option.';

// The template, then the standard rules and rule options.
const RULE_COUNT = 1 + STANDARD_RULES.length + RULE_OPTIONS.length;

// Filled down each column then across, so the wizard's order reads top to bottom: four columns of
// five or six on a wide screen.
const gridByBreakpoint = toValue =>
  Object.fromEntries(
    Object.entries({ xs: 1, sm: 2, md: 4 }).map(([breakpoint, columns]) => [
      breakpoint,
      toValue(columns),
    ]),
  );

const GRID_SX = {
  display: 'grid',
  gridAutoFlow: 'column',
  gridTemplateColumns: gridByBreakpoint(columns => `repeat(${columns}, minmax(0, 1fr))`),
  gridTemplateRows: gridByBreakpoint(columns => `repeat(${Math.ceil(RULE_COUNT / columns)}, auto)`),
  columnGap: 3,
  rowGap: 0.75,
};

const toState = value => (value ? RULE_STATE.On : RULE_STATE.Off);

// The base wizard's choices in the order it asks them: the template and our standard rules, which
// only a portal built base records, then every rule option it offers, of which a supplemental can
// only hold some.
const buildRules = (version, isSupplemental) => {
  const portalOptions = version.portalOptions;

  return [
    {
      key: 'template',
      label: portalOptions
        ? `${getBasePolicyTemplateLabel(portalOptions.template)} template`
        : 'Template',
      description: portalOptions ? 'The template the policy was built from.' : NOT_APPLICABLE_NOTE,
      state: portalOptions ? RULE_STATE.On : RULE_STATE.NotApplicable,
    },
    ...STANDARD_RULES.map(rule => ({
      key: rule.value,
      label: rule.label,
      description: portalOptions ? rule.description : NOT_APPLICABLE_NOTE,
      state: portalOptions ? toState(portalOptions[rule.value]) : RULE_STATE.NotApplicable,
    })),
    ...RULE_OPTIONS.map(option => {
      const applies = !isSupplemental || isPolicyRuleOptionValidInSupplemental(option.value);
      return {
        key: `ruleOption-${option.value}`,
        number: option.value,
        label: option.label,
        description: applies ? option.description : SUPPLEMENTAL_NOTE,
        state: applies
          ? toState(version.ruleOptionNumbers?.includes(option.value))
          : RULE_STATE.NotApplicable,
      };
    }),
  ];
};

const RuleItem = ({ rule }) => {
  const { icon, color } = RULE_STATE_ICONS[rule.state];

  return (
    <Tooltip title={rule.description} placement="top-start">
      <Stack direction="row" alignItems="flex-start" gap={0.75} sx={{ minWidth: 0 }}>
        <Iconify icon={icon} width={14} height={14} color={color} sx={{ flexShrink: 0, mt: 0.1 }} />
        <Typography
          sx={{
            fontSize: '0.7rem',
            lineHeight: 1.35,
            wordBreak: 'break-word',
            color: rule.state === RULE_STATE.NotApplicable ? '#9CA3AF' : 'inherit',
          }}>
          {rule.number !== undefined ? (
            <Box component="span" sx={{ color: '#9CA3AF', display: 'inline-block', minWidth: 20 }}>
              {rule.number}
            </Box>
          ) : null}
          {rule.label}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

const PolicyVersionRules = ({ version, isSupplemental }) => (
  <Box sx={GRID_SX}>
    {buildRules(version, isSupplemental).map(rule => (
      <RuleItem key={rule.key} rule={rule} />
    ))}
  </Box>
);

export default PolicyVersionRules;
